import { Trans } from "@lingui/macro";
import {
	Button,
	Container,
	Drawer,
	Group,
	Image,
	Modal,
	Stack,
	Text,
} from "@mantine/core";
import { useNavigate } from "@remix-run/react";
import dayjs from "dayjs";
import duration, { type Duration } from "dayjs/plugin/duration";
import { ArrowLeft, Home, LogOut, Settings, User } from "lucide-react";
import { useEffect, useState } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { LocaleSelector } from "~/routes/_index/lingui";
import { usePortalFlowCheck } from "~/utils/portal-api.client";
import { usePortalState } from "~/utils/states";
import PortalLoginModal from "./portal-login-modal";

dayjs.extend(duration);

interface PortalHeaderProps {
	showLanguageSelector?: boolean;
	showBackButton?: boolean;
	onBack?: () => void;
	allowUnauthenticated?: boolean; // Allow viewing page without authentication (for portal flow)
}

export function PortalHeader({
	showLanguageSelector = false,
	showBackButton = false,
	onBack,
	allowUnauthenticated = false,
}: PortalHeaderProps) {
	const { selectedTenant } = usePortalFlowCheck();
	const {
		logout,
		isAuthenticated,
		portalPatient,
		tokenExpiry,
		extendSession,
		resetToMenu,
	} = usePortalState();
	const navigate = useNavigate();

	// Session timer state
	const [timeRemaining, setTimeRemaining] = useState<Duration | null>(null);
	const [userSidebarOpened, setUserSidebarOpened] = useState(false);
	const [logoutModalOpened, setLogoutModalOpened] = useState(false);
	const [loginModalOpened, setLoginModalOpened] = useState(false);

	// Session timer effect
	useEffect(() => {
		if (!isAuthenticated || !tokenExpiry) {
			setTimeRemaining(null);
			return;
		}

		const calculateTimeRemaining = () => {
			const now = dayjs();
			const expiry = dayjs(tokenExpiry);
			const remaining = expiry.diff(now);
			return dayjs.duration(remaining);
		};

		const initialRemaining = calculateTimeRemaining();
		setTimeRemaining(initialRemaining);

		const interval = setInterval(() => {
			const remaining = calculateTimeRemaining();
			const seconds = remaining.asSeconds();

			if (seconds > 0) {
				setTimeRemaining(remaining);
			} else {
				// Session expired - auto logout
				setTimeRemaining(dayjs.duration(0));
				clearInterval(interval);
				setTimeout(() => {
					logout();
				}, 100);
			}
		}, 1000); // Check every second for accurate countdown and immediate logout

		return () => clearInterval(interval);
	}, [isAuthenticated, tokenExpiry, logout]);

	// Auto redirect when user is no longer authenticated
	// SKIP this check if allowUnauthenticated is true (for portal browsing flow)
	useEffect(() => {
		if (!isAuthenticated && !allowUnauthenticated) {
			// Use setTimeout to avoid React DOM manipulation conflicts
			setTimeout(() => {
				navigate("/", { replace: true });
			}, 100);
		}
	}, [isAuthenticated, navigate, allowUnauthenticated]);

	// Logout confirmation handlers (same pattern as portal-landing)
	const handleLogoutConfirmation = () => {
		setLogoutModalOpened(true);
	};

	const handleLogoutConfirm = () => {
		setLogoutModalOpened(false);
		// Navigate to home first, then logout to avoid blank screen
		navigate("/", { replace: true });
		// Use setTimeout to avoid React DOM manipulation conflicts
		setTimeout(() => {
			logout();
		}, 100);
	};

	const handleLogoutCancel = () => {
		setLogoutModalOpened(false);
	};

	return (
		<div>
			{/* Top Bar - Session Timer */}
			<div
				style={{
					backgroundColor: "#1f2937",
					color: "white",
					padding: "8px 0",
					fontSize: "14px",
				}}
			>
				<Container size="xl">
					{/* Session Timer - Full width center */}
					{isAuthenticated && timeRemaining ? (
						<div
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
								width: "100%",
								minHeight: "24px",
							}}
						>
							<ClientOnly fallback={<div>...</div>}>
								{() => {
									const DD = timeRemaining.days();
									const HH = timeRemaining.hours();
									const mm = timeRemaining.minutes();
									const totalSeconds = timeRemaining.asSeconds();

									if (totalSeconds < 60) {
										return (
											<div>
												<span style={{ color: "#ef4444", fontSize: "11px" }}>
													<Trans>
														Session akan berakhir dalam{" "}
														{Math.ceil(totalSeconds)} detik
													</Trans>
												</span>
											</div>
										);
									}

									return (
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												gap: "8px",
												flexWrap: "nowrap",
												whiteSpace: "nowrap",
											}}
										>
											<span
												style={{
													fontSize: "11px",
													display: "inline-block",
													lineHeight: "1.2",
												}}
											>
												<Trans>
													Your session will expire in <strong>{DD}</strong> days{" "}
													<strong>{HH}</strong> hours <strong>{mm}</strong>{" "}
													minutes
												</Trans>
											</span>
											<button
												type="button"
												onClick={() => extendSession()}
												style={{
													background: "#3b82f6",
													color: "white",
													border: "none",
													borderRadius: "4px",
													padding: "2px 6px",
													fontSize: "10px",
													cursor: "pointer",
													flexShrink: 0,
													whiteSpace: "nowrap",
												}}
											>
												+1h
											</button>
										</div>
									);
								}}
							</ClientOnly>
						</div>
					) : (
						<div style={{ height: "24px", width: "100%" }} />
					)}
				</Container>
			</div>

			{/* Portal Header */}
			<div
				style={{
					background: "white",
					borderBottom: "1px solid #e5e7eb",
					boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
				}}
			>
				<Container size="xl" py="lg" className="portal-header-container">
					{/* Responsive CSS */}
					<style>
						{`
							@media (min-width: 768px) {
								.portal-header-mobile {
									display: none !important;
								}
								.portal-header-desktop {
									display: flex !important;
								}
							}
							@media (max-width: 767px) {
								.portal-header-mobile {
									display: flex !important;
								}
								.portal-header-desktop {
									display: none !important;
								}
								.portal-header-container {
									padding-left: 0 !important;
									padding-right: 0 !important;
								}
								.portal-header-mobile-content {
									padding-left: 16px;
									padding-right: 16px;
								}
							}
						`}
					</style>

					{/* Desktop Layout */}
					<Group
						justify="space-between"
						align="center"
						className="portal-header-desktop"
						style={{ display: "none" }}
					>
						<div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
							{showBackButton && (
								<Button
									variant="light"
									size="sm"
									leftSection={<ArrowLeft size={16} />}
									onClick={
										onBack ||
										(() => {
											resetToMenu(); // Clear all branch-specific state
											navigate("/");
										})
									}
									styles={{
										root: {
											borderRadius: "8px",
											border: "1px solid #e5e7eb",
										},
									}}
								>
									<Trans>Kembali</Trans>
								</Button>
							)}
							<Image
								src="/logo-tm-kemenkes.png"
								alt="Company Logo"
								h={40}
								fit="contain"
							/>
						</div>

						{/* Right section with language selector and login/profile */}
						<Group gap="md" align="center">
							{/* Language Selector - Desktop only */}
							{showLanguageSelector && (
								<div style={{ transform: "scale(0.9)" }}>
									<LocaleSelector />
								</div>
							)}

							{/* Login Button - Show if not authenticated */}
							{!isAuthenticated && (
								<Button
									variant="filled"
									color="blue"
									size="md"
									leftSection={<User size={18} />}
									onClick={() => setLoginModalOpened(true)}
									style={{
										background: "#1e3a8a",
									}}
								>
									<Trans>Masuk</Trans>
								</Button>
							)}

							{/* User Profile Icon - Show if authenticated */}
							{isAuthenticated && portalPatient && (
								<div
									onClick={() => setUserSidebarOpened(true)}
									style={{
										width: "36px",
										height: "36px",
										borderRadius: "50%",
										background: "linear-gradient(45deg, #3b82f6, #1d4ed8)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										color: "white",
										fontSize: "15px",
										fontWeight: "600",
										boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
										cursor: "pointer",
										transition: "all 0.2s ease",
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.transform = "scale(1.05)";
										e.currentTarget.style.boxShadow =
											"0 4px 12px rgba(59, 130, 246, 0.4)";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.transform = "scale(1)";
										e.currentTarget.style.boxShadow =
											"0 2px 8px rgba(59, 130, 246, 0.3)";
									}}
								>
									{portalPatient?.full_name?.charAt(0)?.toUpperCase()}
								</div>
							)}
						</Group>
					</Group>

					{/* Mobile Layout */}
					<div className="portal-header-mobile" style={{ display: "none" }}>
						<div
							className="portal-header-mobile-content"
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								width: "100%",
							}}
						>
							<Image
								src="/logo-tm-kemenkes.png"
								alt="Trustmedis Logo"
								h={40}
								fit="contain"
							/>

							{/* Login Button or User Avatar - Mobile (RIGHT/pojok kanan) */}
							{!isAuthenticated ? (
								<Button
									variant="filled"
									color="blue"
									size="sm"
									leftSection={<User size={16} />}
									onClick={() => setLoginModalOpened(true)}
									style={{
										background: "#1e3a8a",
									}}
								>
									<Trans>Masuk</Trans>
								</Button>
							) : (
								portalPatient && (
									<div
										onClick={() => setUserSidebarOpened(true)}
										style={{
											width: "32px",
											height: "32px",
											borderRadius: "50%",
											background: "linear-gradient(45deg, #3b82f6, #1d4ed8)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "white",
											fontSize: "14px",
											fontWeight: "600",
											boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
											cursor: "pointer",
										}}
									>
										{portalPatient?.full_name?.charAt(0)?.toUpperCase()}
									</div>
								)
							)}
						</div>
					</div>
				</Container>
			</div>

			{/* User Profile Sidebar */}
			<Drawer
				opened={userSidebarOpened}
				onClose={() => setUserSidebarOpened(false)}
				position="right"
				size="320px"
				title={
					<Group gap="sm">
						<div
							style={{
								width: "32px",
								height: "32px",
								borderRadius: "50%",
								background: "linear-gradient(45deg, #3b82f6, #1d4ed8)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: "white",
								fontSize: "14px",
								fontWeight: "600",
							}}
						>
							{portalPatient?.full_name?.charAt(0)?.toUpperCase()}
						</div>
						<div>
							<Text size="sm" fw={600}>
								<Trans>Profil Pengguna</Trans>
							</Text>
						</div>
					</Group>
				}
				styles={{
					header: {
						borderBottom: "1px solid #e5e7eb",
						paddingBottom: "1rem",
						marginBottom: "1rem",
					},
				}}
			>
				<Stack gap="lg">
					{/* User Info Section */}
					<div
						style={{
							background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)",
							borderRadius: "16px",
							padding: "20px",
							boxShadow: "0 2px 8px rgba(9, 60, 113, 0.08)",
							transition: "all 0.3s ease",
						}}
					>
						<Group gap="md" align="flex-start">
							<div
								style={{
									width: "56px",
									height: "56px",
									borderRadius: "50%",
									background:
										"linear-gradient(135deg, #093c71 0%, #1e40af 100%)",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									color: "white",
									fontSize: "22px",
									fontWeight: "600",
									boxShadow: "0 4px 16px rgba(9, 60, 113, 0.25)",
									flexShrink: 0,
								}}
							>
								{portalPatient?.full_name?.charAt(0)?.toUpperCase()}
							</div>
							<div style={{ flex: 1, minWidth: 0 }}>
								<Text
									size="lg"
									fw={600}
									c="#093c71"
									style={{
										marginBottom: "4px",
										lineHeight: "1.3",
									}}
								>
									{portalPatient?.full_name}
								</Text>
								<Text
									size="sm"
									c="#093c71"
									style={{
										opacity: 0.65,
										marginBottom: "2px",
										fontSize: "13px",
									}}
								>
									NIK: {portalPatient?.nik}
								</Text>
								<Text
									size="xs"
									c="#093c71"
									style={{
										opacity: 0.45,
										fontSize: "11px",
									}}
								>
									ID: {portalPatient?.global_patient_id}
								</Text>
							</div>
						</Group>
					</div>

					{/* Language Selector Section - Mobile Only */}
					{showLanguageSelector && (
						<div
							style={{
								background: "#ffffff",
								borderRadius: "16px",
								padding: "20px",
								boxShadow: "0 2px 8px rgba(9, 60, 113, 0.08)",
							}}
						>
							<Text
								size="sm"
								fw={600}
								c="#093c71"
								style={{ marginBottom: "12px" }}
							>
								<Trans>Bahasa</Trans>
							</Text>
							<LocaleSelector />
						</div>
					)}

					{/* Actions Section */}
					<div>
						<Text
							size="sm"
							fw={600}
							c="#093c71"
							style={{ marginBottom: "12px", paddingLeft: "4px" }}
						>
							<Trans>Aksi</Trans>
						</Text>
						<Stack gap="sm">
							<Button
								variant="subtle"
								size="md"
								leftSection={
									<div
										style={{
											width: "32px",
											height: "32px",
											borderRadius: "8px",
											background:
												"linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<Home size={16} color="#093c71" />
									</div>
								}
								onClick={() => {
									setUserSidebarOpened(false);
									// Use window.location to force full page reload and avoid state conflicts
									setTimeout(() => {
										window.location.href = "/";
									}, 100);
								}}
								fullWidth
								styles={{
									root: {
										color: "#093c71",
										backgroundColor: "transparent",
										border: "none",
										borderRadius: "12px",
										fontWeight: 500,
										padding: "12px 16px",
										height: "auto",
										transition: "all 0.2s ease",
										"&:hover": {
											backgroundColor: "rgba(240, 249, 255, 0.5)",
											transform: "translateX(4px)",
										},
									},
									inner: {
										justifyContent: "flex-start",
										gap: "12px",
									},
									section: {
										marginRight: 0,
									},
								}}
							>
								<Trans>Kembali ke Beranda</Trans>
							</Button>

							<Button
								variant="subtle"
								size="md"
								leftSection={
									<div
										style={{
											width: "32px",
											height: "32px",
											borderRadius: "8px",
											background:
												"linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<ArrowLeft size={16} color="#093c71" />
									</div>
								}
								onClick={() => {
									setUserSidebarOpened(false);
									navigate("/portal-branches");
								}}
								fullWidth
								styles={{
									root: {
										color: "#093c71",
										backgroundColor: "transparent",
										border: "none",
										borderRadius: "12px",
										fontWeight: 500,
										padding: "12px 16px",
										height: "auto",
										transition: "all 0.2s ease",
										"&:hover": {
											backgroundColor: "rgba(240, 249, 255, 0.5)",
											transform: "translateX(4px)",
										},
									},
									inner: {
										justifyContent: "flex-start",
										gap: "12px",
									},
									section: {
										marginRight: 0,
									},
								}}
							>
								<Trans>Ganti Cabang</Trans>
							</Button>

							<Button
								variant="subtle"
								size="md"
								leftSection={
									<div
										style={{
											width: "32px",
											height: "32px",
											borderRadius: "8px",
											background:
												"linear-gradient(135deg, #f0f9ff 0%, #dbeafe 100%)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<Settings size={16} color="#093c71" />
									</div>
								}
								onClick={() => {
									setUserSidebarOpened(false);
									navigate("/portal-profile");
								}}
								fullWidth
								styles={{
									root: {
										color: "#093c71",
										backgroundColor: "transparent",
										border: "none",
										borderRadius: "12px",
										fontWeight: 500,
										padding: "12px 16px",
										height: "auto",
										transition: "all 0.2s ease",
										"&:hover": {
											backgroundColor: "rgba(240, 249, 255, 0.5)",
											transform: "translateX(4px)",
										},
									},
									inner: {
										justifyContent: "flex-start",
										gap: "12px",
									},
									section: {
										marginRight: 0,
									},
								}}
							>
								<Trans>Pengaturan Profil</Trans>
							</Button>

							<Button
								variant="filled"
								size="md"
								leftSection={
									<div
										style={{
											width: "32px",
											height: "32px",
											borderRadius: "8px",
											background: "rgba(255, 255, 255, 0.2)",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<LogOut size={16} color="white" />
									</div>
								}
								onClick={() => {
									setUserSidebarOpened(false);
									handleLogoutConfirmation();
								}}
								fullWidth
								styles={{
									root: {
										background:
											"linear-gradient(135deg, #093c71 0%, #1e40af 100%)",
										color: "white",
										border: "none",
										borderRadius: "12px",
										fontWeight: 500,
										padding: "12px 16px",
										height: "auto",
										transition: "all 0.3s ease",
										boxShadow: "0 2px 8px rgba(9, 60, 113, 0.15)",
										"&:hover": {
											background:
												"linear-gradient(135deg, #0a4a8a 0%, #2563eb 100%)",
											boxShadow: "0 4px 16px rgba(9, 60, 113, 0.25)",
											transform: "translateY(-2px)",
										},
									},
									inner: {
										justifyContent: "flex-start",
										gap: "12px",
									},
									section: {
										marginRight: 0,
									},
								}}
							>
								<Trans>Keluar dari Portal</Trans>
							</Button>
						</Stack>
					</div>
				</Stack>
			</Drawer>

			{/* Logout Confirmation Modal (same pattern as portal-landing) */}
			<Modal
				opened={logoutModalOpened}
				onClose={handleLogoutCancel}
				title={<Trans>Konfirmasi Logout</Trans>}
				size="sm"
				centered
			>
				<Stack gap="md">
					<Text>
						<Trans>
							Apakah Anda yakin ingin keluar dari portal? Anda perlu login ulang
							untuk mengakses portal.
						</Trans>
					</Text>

					<Group justify="flex-end" gap="sm">
						<Button variant="outline" color="gray" onClick={handleLogoutCancel}>
							<Trans>Batal</Trans>
						</Button>
						<Button color="red" onClick={handleLogoutConfirm}>
							<Trans>Ya, Keluar</Trans>
						</Button>
					</Group>
				</Stack>
			</Modal>

			{/* Portal Login Modal */}
			<PortalLoginModal
				opened={loginModalOpened}
				onClose={() => setLoginModalOpened(false)}
				tenantId={selectedTenant?.tenant_id || "portal"}
				onSelectBranchForSync={(syncData) => {
					navigate("/portal-sync", {
						state: { syncMode: true, syncData },
					});
				}}
			/>
		</div>
	);
}
